import React from "react";
import { Link } from "react-router-dom";
import useGetFeaturedBooks from "api/BookResource/useGetFeaturedBooks";
import useTrendingBooks from "api/BookResource/useTrendingBooks";
import useAuthStore from "store/authStore";
import WavyLoader from "components/v2/WavyLoader";
import { FaRegThumbsUp, FaArrowRight, FaBookOpen, FaRocket, FaFire } from "react-icons/fa";
import { BookListItem } from "components";

const FeaturedBooks = () => {
	const loggedIn = useAuthStore((state) => state.loggedIn);
	const { data, isLoading } = useGetFeaturedBooks({
		size: 12,
	});
	const { data: trendingBooks, isLoading: isLoadingTrending } = useTrendingBooks({
		period: "weekly",
		limit: 12,
	});

	const latest = data?.latestBooks || [];
	const recommended = data?.recommendedBooks || [];
	const trending = Array.isArray(trendingBooks) ? trendingBooks : [];

	if (isLoading) {
		return (
			<div className="py-20">
				<WavyLoader width="100%" height={200} />
			</div>
		);
	}

	const SectionHeader = ({ icon: Icon, title, subtitle, color = "akgreener" }) => (
		<div className="text-left mb-12">
			<div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full text-gray-600 font-medium mb-2">
				<Icon className="w-4 h-4 mr-2" />
				{title}
			</div>
			<h2 className="text-2l lg:text-2xl font-bold text-gray-600 mb-4">{subtitle}</h2>
		</div>
	);

	const BookGrid = ({ books, className = "", showWishlist = false }) => (
		<div
			className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 ${className}`}
		>
			{books.map((book) => (
				<BookListItem
					key={book.id}
					coverUrl={book?.book_cover?.url}
					title={book?.title}
					description={book?.description}
					price={book?.price}
					zwl_price={book?.zwl_price}
					credits={book.credits}
					publisher={book?.publisher || `${book?.user?.first_name} ${book?.user?.last_name}`}
					author={book?.author}
					reviews={book.ratings}
					bookId={book.id}
					sellable={book.sellable}
					rating={book.rating}
					isbn={book.rating}
					isPurchased={book.is_purchased}
					inLibrary={book.in_library}
					subscription={book.subscription}
					category={book?.category?.name}
					isFree={book?.isFree}
					format={book?.format}
					genres={book?.genres?.map((g) => [g?.id, g?.name]) ?? book?.genre?.map((genre) => [genre.id, genre.name])}
					showWishlist={showWishlist}
					isInWishlist={book?.in_wishlist}
					hasPreview={book?.has_preview}
				/>
			))}
		</div>
	);

	return (
		<div className="max-w-7xl mx-auto">
			{/* Trending Now Section */}
			{!isLoadingTrending && trending.length > 0 && (
				<section className="mb-20">
					<SectionHeader
						icon={FaFire}
						title="Popular This Week"
						subtitle="Trending Now"
					/>
					<BookGrid books={trending} showWishlist={loggedIn} />
				</section>
			)}

			{/* Recommended Books Section */}
			{recommended.length > 0 && (
				<section className="mb-20">
					<SectionHeader
						icon={FaRegThumbsUp}
						title="Curated Selection"
						subtitle="Recommended for You"
					/>
					<BookGrid books={recommended} showWishlist={loggedIn} />
				</section>
			)}

			{/* Latest Books Section */}
			{latest.length > 0 && (
				<section className="mb-10">
					<SectionHeader icon={FaRocket} title="Fresh Arrivals" subtitle="Latest Books" />
					<BookGrid books={latest} showWishlist={loggedIn} />
				</section>
			)}

			{/* Call to Action */}
			<section className="text-center py-16 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl">
				<div className="max-w-2xl mx-auto">
					<FaBookOpen className="w-16 h-16 text-akgreener mx-auto mb-6" />
					<h3 className="text-2xl lg:text-3xl font-cera-black text-gray-600 mb-4">
						Can't Find What You're Looking For?
					</h3>
					<p className="text-base text-gray-600 mb-8">
						Browse our complete collection of over 10,000 books across all genres and categories.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Link
							to="/books"
							className="inline-flex items-center px-8 py-4 bg-akgreener text-white rounded-full font-bold text-lg hover:bg-akgreenest hover:scale-105 transition-all duration-300 shadow-lg"
						>
							<span>Browse All Books</span>
							<FaArrowRight className="ml-2 w-4 h-4" />
						</Link>
					</div>
				</div>
			</section>
		</div>
	);
};

export default FeaturedBooks;
